import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import "./Footer.css";
import { ConfigContext } from "../Context/ConfigContext";

export default function Footer() {
  const { ApiUrl, apiHeaderJson, social_media, createSlug } = useContext(ConfigContext);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Newsletter state
  const [newsletterPhone, setNewsletterPhone] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState("");

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${ApiUrl}apis/CategoryList`, {
          headers: apiHeaderJson,
        });
        const { success, data } = response.data;
        if (success && Array.isArray(data)) {
          const mapped = data.map((item) => ({
            id: item.category_id || item.id,
            name: item.category_title || item.name,
          }));
          setCategories(mapped);
        } else {
          setCategories([]);
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        setError(err.message);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [ApiUrl, apiHeaderJson]);

  // Handle newsletter subscription
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!newsletterPhone.trim()) {
      setNewsletterMessage("Please enter a phone number.");
      return;
    }

    setNewsletterLoading(true);
    setNewsletterMessage("");

    try {
      const payload = { phone_no: newsletterPhone.trim() };
      const response = await axios.post(
        `${ApiUrl}apis/SendNewsletterMail`,
        payload,
        { headers: apiHeaderJson }
      );

      const { success, message } = response.data;
      if (success) {
        setNewsletterMessage("Subscription successful! You'll receive updates.");
        setNewsletterPhone(""); // Clear input
      } else {
        setNewsletterMessage(message || "Subscription failed. Please try again.");
      }
    } catch (err) {
      console.error("Newsletter error:", err);
      setNewsletterMessage("Something went wrong. Please try again later.");
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <>
      {/* Animated Waves */}
      <div className="footer-waves">
        <svg
          className="waves"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 24 150 28"
          preserveAspectRatio="none"
        >
          <defs>
            <path
              id="wave-path"
              d="M-160 44c30 0 58-18 88-18s58 18 88 18
              58-18 88-18 58 18 88 18v44h-352z"
            />
          </defs>
          <g className="parallax">
            <use href="#wave-path" x="48" y="0"></use>
            <use href="#wave-path" x="48" y="3"></use>
            <use href="#wave-path" x="48" y="5"></use>
            <use href="#wave-path" x="48" y="7"></use>
          </g>
        </svg>
      </div>

      <footer className="footer">
        <div className="container">
          <div className="row gy-5">
            {/* Brand */}
            <div className="col-lg-4 col-md-12">
              <div className="footer-brand">
                <div className="footer-logo">
                  <img
                    src="/icon-logo.png"
                    alt="Icon Logo"
                    className="footer-icon-logo"
                  />
                  <img
                    src="/royal-logo.png"
                    alt="Royal Logo"
                    className="footer-royal-logo"
                  />
                </div>
                <div className="footer-social">
                  <Link target="_blank" to={social_media.facebook} aria-label="Facebook">
                    <i className="ri-facebook-fill"></i>
                  </Link>
                  <Link target="_blank" to={social_media.instagram} aria-label="Instagram">
                    <i className="ri-instagram-line"></i>
                  </Link>
                  <Link target="_blank" to={social_media.twitter} aria-label="Twitter">
                    <i className="ri-twitter-x-line"></i>
                  </Link>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="col-lg-2 col-md-4">
              <h4>Quick Links</h4>
              <ul className="footer-links">
                <li><Link to="/">Home</Link></li>
                <li><Link to="/about">About</Link></li>
                <li><Link to="/products">Products</Link></li>
                <li><Link to="/inquiry">Inquiry</Link></li>
                <li><Link to="/contact">Contact</Link></li>
              </ul>
            </div>

            {/* Categories - Dynamically loaded from API */}
            <div className="col-lg-3 col-md-4">
              <h4>Categories</h4>
              <ul className="footer-links">
                {loading ? (
                  <li>Loading...</li>
                ) : error || categories.length === 0 ? (
                  <li>No categories</li>
                ) : (
                  categories.map((cat) => (
                    <li key={cat.id}>
                      <Link to={`/products/${createSlug(cat.name)}-${cat.id}`}>{cat.name}</Link>
                    </li>
                  ))
                )}
              </ul>
            </div>

            <div className="col-lg-3 col-md-4">
              <h4>Registration</h4>
              <p>Register with your phone number to receive future updates.</p>

              <form className="footer-form" onSubmit={handleNewsletterSubmit}>
                <input
                  type="tel"
                  placeholder="Enter phone number"
                  value={newsletterPhone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setNewsletterPhone(value);
                  }}
                  maxLength={10}
                  disabled={newsletterLoading}
                  required
                />

                <button type="submit" disabled={newsletterLoading}>
                  {newsletterLoading ? "Registering..." : "Register"}
                </button>
              </form>

              {newsletterMessage && (
                <p
                  className={`footer-newsletter-message ${newsletterMessage.toLowerCase().includes("successful")
                    ? "success"
                    : "error"
                    }`}
                >
                  {newsletterMessage}
                </p>
              )}
            </div>
          </div>

          <hr />
          <div className="footer-bottom">
            © {new Date().getFullYear()} Royal Bakery. All Rights Reserved.
          </div>
        </div>
      </footer>
    </>
  );
}